import { CordovaPlugins } from '../CordovaPlugins';
import { isAndroid, isConnected, isIos, isWeb, isWindows } from './is';
import { confirmNotification } from './notifications';

/**
 * Our versioning information so we can update via Trigger and
 * via the AppStore's more or less automatically with only having to
 * deploy new code to our servers
 */

const ANDROID_MARKET_URI = 'market://details?id=com.zenput.mobile';
const IOS_MARKET_URI = 'itms-apps://itunes.apple.com/en/app/zenput/id657230654';
const WINDOWS_MARKET_URI = 'ms-windows-store://pdp/?productid=9p9fmqjwhln3';

/**
 * Compares two software version numbers (e.g. "1.7.1" or "1.2b").
 *
 * This function was born in http://stackoverflow.com/a/6832721.
 *
 * @param {string} v1 The first version to be compared.
 * @param {string} v2 The second version to be compared.
 * @param {object} [options] Optional flags that affect comparison behavior:
 *      "lexicographical: true" compares each part of the version strings lexicographically instead of
 *         naturally; this allows suffixes such as "b" or "dev" but will cause "1.10" to be considered smaller than
 *         "1.2".
 *      "zeroExtend: true" changes the result if one version string has less parts than the other. In
 *         this case the shorter string will be padded with "zero" parts instead of being considered smaller.
 *
 * @returns {number|NaN}
 *    0 if the versions are equal
 *    a negative integer iff v1 < v2
 *    a positive integer iff v1 > v2
 *    NaN if either version string is in the wrong format
 *
 * @copyright by Jon Papaioannou (["john", "papaioannou"].join(".") + "@gmail.com")
 * @license This function is in the public domain. Do what you want with it, no strings attached.
 */
export function compareVersions(
    v1: string,
    v2: string,
    options?: { lexicographical: boolean; zeroExtend: boolean }
): 0 | 1 | -1 | typeof NaN {
    const lexicographical = options && options.lexicographical;
    const zeroExtend = options && options.zeroExtend;
    let v1parts: string[] | number[] = v1.split('.');
    let v2parts: string[] | number[] = v2.split('.');

    function isValidPart(x) {
        return (lexicographical ? /^\d+[A-Za-z]*$/ : /^\d+$/).test(x);
    }

    if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) {
        return NaN;
    }

    if (zeroExtend) {
        while (v1parts.length < v2parts.length) {
            v1parts.push('0');
        }
        while (v2parts.length < v1parts.length) {
            v2parts.push('0');
        }
    }

    if (!lexicographical) {
        v1parts = v1parts.map(Number);
        v2parts = v2parts.map(Number);
    }

    for (let i = 0; i < v1parts.length; ++i) {
        if (v2parts.length == i) {
            return 1;
        }

        if (v1parts[i] == v2parts[i]) {
            continue;
        } else if (v1parts[i] > v2parts[i]) {
            return 1;
        } else {
            return -1;
        }
    }

    if (v1parts.length != v2parts.length) {
        return -1;
    }

    return 0;
}

export function downloadApp() {
    if (isAndroid()) {
        $f.mobile.open_uri(ANDROID_MARKET_URI);
    } else if (isIos()) {
        $f.mobile.open_uri(IOS_MARKET_URI);
    } else if (isWindows()) {
        $f.mobile.open_uri(WINDOWS_MARKET_URI);
    }
}

/**
 * Check for an app update.
 */
export const checkForUpdate = () => {
    if (isWeb() || !isConnected()) {
        return;
    }

    $f.mobile.settings.get('latest_appstore_version', (latest_appstore_version?: string) => {
        if (!latest_appstore_version) {
            return;
        }
        if (compareVersions(CordovaPlugins().BuildInfo.version, latest_appstore_version) >= 0) {
            return;
        }

        $f.mobile.settings.get('date_upgrade_notified', (date_upgrade_notified?: number) => {
            const now = new Date().getTime();
            if (date_upgrade_notified == undefined || !date_upgrade_notified) {
                // eslint-disable-next-line no-param-reassign
                date_upgrade_notified = now - (259200000 + 1); // set last updated to just over 3 days ago
                $f.mobile.settings.set('date_upgrade_notified', date_upgrade_notified);
            }

            if (now - date_upgrade_notified < 259200000) {
                return;
            } // only remind them every 3 days (259200000ms)

            $f.mobile.settings.set('date_upgrade_notified', now);

            let store_name = 'invalid_platform';
            if (isIos()) {
                store_name = _t('mobile_message_store_upgrade_store_ios');
            } else if (isAndroid()) {
                store_name = _t('mobile_message_store_upgrade_store_android');
            } else if (isWindows()) {
                store_name = 'Windows Store'; // we don't localize Play Store, so leaving Windows unlocalized as well
            }

            confirmNotification(
                `${_t('mobile_message_store_upgrade_confirm_message_one')}${store_name}${_t(
                    'mobile_message_store_upgrade_confirm_message_two'
                )}`,
                function (success) {
                    if (success) {
                        downloadApp();
                    }
                },
                _t('mobile_message_store_upgrade_confirm_title'),
                _t('mobile_message_store_upgrade_confirm_now'),
                _t('mobile_message_store_upgrade_confirm_later')
            );
        });
    });
};
